
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useSelecionarBase } from '@/core/composables/SelecionarBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoControleCaixas from '@/servicos/Financeiro/ServicoControleCaixas';

export default defineComponent({
  name: 'SelecionarAberturaCaixa',
  props: {
    codigoSelecionado: {
      type: Number || undefined,
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    caixa: {
      type: Number,
      required: true,
    },
    usuario: {
      type: Number,
      required: true,
    },
    data: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Selecione',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    dataAttributeColuna: {
      type: String,
      default: '',
    },
    classCss: {
      type: String,
      default: '',
    },
    limparSelecao: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'update:descricaoSelecionado', 'change'],
  setup(props, { emit }) {
    const {
      selecionarBase, montaOpcoesComListaConsultaRapida, verificacaoPreBusca, verificacaoUltimaPesquisa, aguardarConclusaoCarregamento,
      comportamentoPadraoSemResultado,
    } = useSelecionarBase(props, emit);

    const refSelecionar = ref<HTMLElement | null>(null);

    let debounce = 0;
    const servico = new ServicoControleCaixas();

    function obterCorStatus(status: string) {
      let cor = '';
      if (status === 'Aberto') {
        cor = '--cor-verde';
      } else if (status === 'Fechado') {
        cor = '--cor-vermelha';
      } else {
        cor = '--cor-amarela';
      }
      return cor;
    }

    function preencheListaOpcoes(): void {
      if (UtilitarioGeral.validaLista(selecionarBase.listaItensConsultaRapida)) {
        selecionarBase.listaOpcoes = montaOpcoesComListaConsultaRapida(selecionarBase.listaItensConsultaRapida);
      }
    }

    async function obterPorCodigo(valor: any) {
      if (!verificacaoPreBusca(valor)) return;

      selecionarBase.listaOpcoes = [];
      selecionarBase.listaItensConsultaRapida = await servico.consultaRapida(props.caixa, props.usuario, props.data);
      preencheListaOpcoes();
      comportamentoPadraoSemResultado();
    }

    async function pesquisar(valorPesquisa: any) {
      selecionarBase.emDigitacao = true;

      if (!verificacaoUltimaPesquisa(valorPesquisa)) return;
      selecionarBase.buscandoDados = true;
      selecionarBase.listaOpcoes = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        selecionarBase.listaItensConsultaRapida = await servico.consultaRapida(props.caixa, props.usuario, props.data);
        preencheListaOpcoes();
        selecionarBase.valorUltimaPesquisa = valorPesquisa;
        selecionarBase.buscandoDados = false;
      }, 600);
    }

    function obter(): any {
      obterPorCodigo(props.codigoSelecionado);
      return (props.codigoSelecionado === 0 ? undefined : props.codigoSelecionado);
    }

    const computedDescricaoSelecionado = computed({
      get: () => props.descricaoSelecionado,
      set: (val: string) => {
        emit('update:descricaoSelecionado', val);
      },
    });

    const computedCodigoSelecionado = computed({
      get: () => obter(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
        const itemConsulta = selecionarBase.listaItensConsultaRapida.find((c) => c.codigo === val);
        if (itemConsulta?.textoIdentificacao !== undefined && itemConsulta?.textoIdentificacao !== '') {
          computedDescricaoSelecionado.value = itemConsulta?.textoIdentificacao;
        }
      },
    });

    async function atualizarLista() {
      selecionarBase.listaOpcoes = [];
      computedCodigoSelecionado.value = 0;
      if (props.caixa === 0 || props.caixa === undefined || props.usuario === 0 || props.usuario === undefined || props.data === '' || props.data === undefined) {
        selecionarBase.listaItensConsultaRapida = [];
      } else {
        selecionarBase.listaItensConsultaRapida = await servico.consultaRapida(props.caixa, props.usuario, props.data);
      }
      preencheListaOpcoes();
      comportamentoPadraoSemResultado();
    }

    watch(() => props.caixa, () => {
      atualizarLista();
    });

    watch(() => props.data, () => {
      atualizarLista();
    });

    watch(() => props.usuario, () => {
      atualizarLista();
    });

    function change() {
      emit('change', props.codigoSelecionado);
    }

    function focus() {
      if (refSelecionar.value) {
        refSelecionar.value.focus();
      }
    }

    return {
      props,
      selecionarBase,
      refSelecionar,
      pesquisar,
      aguardarConclusaoCarregamento,
      computedCodigoSelecionado,
      computedDescricaoSelecionado,
      change,
      focus,
      obterCorStatus,
      UtilitarioGeral,
    };
  },
});
